import { styled } from '@mui/material/styles';

import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';

/**
 * The pieces Share My Schedule's two dialogs are built from, on the same
 * ground and in the same type as My Schedule's own dialogs (Export, Clear).
 */

/** My Schedule's dialog ground (`SIDEBAR_BG` in Schedule.tsx). */
export const DIALOG_BG = '#292420';

export const dialogPaperSx = {
  background: DIALOG_BG,
  backgroundImage: 'none',
  color: BRAND.parchment,
  borderRadius: 3,
  p: { xs: 2.5, sm: 3 },
  m: 2,
  width: 'calc(100% - 32px)',
  maxWidth: 440,
} as const;

export const DialogHeading = styled('h2')({
  margin: '0 28px 6px 0',
  fontFamily: FONTS.display,
  fontWeight: 700,
  fontSize: 19,
  lineHeight: 1.25,
});

export const DialogText = styled('p')({
  margin: '0 0 16px',
  fontFamily: FONTS.body,
  fontSize: 13.5,
  lineHeight: 1.5,
  color: 'rgba(243,238,231,0.75)',
});

export const SmallPrint = styled('p')({
  margin: '14px 0 0',
  fontFamily: FONTS.body,
  fontSize: 12,
  lineHeight: 1.5,
  color: 'rgba(243,238,231,0.6)',
});

const pill = {
  appearance: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  width: '100%',
  minHeight: 44,
  padding: '0 18px',
  borderRadius: 999,
  cursor: 'pointer',
  fontFamily: FONTS.body,
  fontSize: 14.5,
  fontWeight: 700,
  textDecoration: 'none',
  boxSizing: 'border-box',
  transition: 'background 120ms ease, border-color 120ms ease',
  '&:disabled': { opacity: 0.5, cursor: 'default' },
} as const;

export const PrimaryAction = styled('button')({
  ...pill,
  border: 'none',
  background: BRAND.parchment,
  color: BRAND.mocha,
  '&:hover:not(:disabled)': { background: '#fff' },
});

export const SecondaryAction = styled('button')({
  ...pill,
  background: 'transparent',
  color: BRAND.parchment,
  border: '1px solid rgba(243,238,231,0.42)',
  '&:hover:not(:disabled)': { borderColor: BRAND.parchment, background: 'rgba(243,238,231,0.07)' },
});

export const SecondaryLink = styled('a')({
  ...pill,
  background: 'transparent',
  color: BRAND.parchment,
  border: '1px solid rgba(243,238,231,0.42)',
  '&:hover': { borderColor: BRAND.parchment, background: 'rgba(243,238,231,0.07)' },
});

export const Stack = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

/** A quieter action: replacing or turning off a link. */
export const TextAction = styled('button')({
  appearance: 'none',
  border: 'none',
  background: 'none',
  padding: '6px 2px',
  cursor: 'pointer',
  fontFamily: FONTS.body,
  fontSize: 13,
  fontWeight: 700,
  color: 'rgba(243,238,231,0.75)',
  textDecoration: 'underline',
  textUnderlineOffset: 3,
  '&:hover:not(:disabled)': { color: BRAND.parchment },
  '&:disabled': { opacity: 0.5, cursor: 'default' },
});

/** The link itself, readable and selectable, one line. */
export const UrlBox = styled('input')({
  width: '100%',
  boxSizing: 'border-box',
  minHeight: 42,
  margin: '0 0 10px',
  padding: '0 12px',
  borderRadius: 10,
  border: '1px solid rgba(243,238,231,0.22)',
  background: 'rgba(243,238,231,0.06)',
  color: BRAND.parchment,
  fontFamily: FONTS.body,
  // 16px or iOS zooms in when it is tapped to select.
  fontSize: 16,
  textOverflow: 'ellipsis',
  '&:focus': { outline: 'none', borderColor: 'rgba(243,238,231,0.6)' },
});

/** An inline "are you sure", in place of the actions it asks about. */
export const Confirm = styled('div')({
  marginTop: 14,
  padding: '12px 14px',
  borderRadius: 12,
  background: 'rgba(243,238,231,0.07)',
  fontFamily: FONTS.body,
  fontSize: 13.5,
  lineHeight: 1.5,
});

export const ErrorText = styled('p')({
  margin: '0 0 12px',
  padding: '9px 12px',
  borderRadius: 10,
  fontFamily: FONTS.body,
  fontSize: 13.5,
  lineHeight: 1.45,
  background: 'rgba(239,61,35,0.16)',
  border: '1px solid rgba(239,61,35,0.6)',
});

export const CloseButton = styled('button')({
  appearance: 'none',
  position: 'absolute',
  top: 12,
  right: 12,
  width: 32,
  height: 32,
  display: 'grid',
  placeItems: 'center',
  border: 'none',
  borderRadius: 999,
  background: 'none',
  cursor: 'pointer',
  color: 'rgba(243,238,231,0.7)',
  fontSize: 20,
  lineHeight: 1,
  '&:hover': { color: BRAND.parchment, background: 'rgba(243,238,231,0.08)' },
});

export const PrimaryLinkAction = styled('a')({
  ...pill,
  border: 'none',
  background: BRAND.parchment,
  color: BRAND.mocha,
  '&:hover': { background: '#fff' },
});
